import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import './CurrentlyWearingSection.css';
import { selectActiveItems } from '../../../redux/slices/clothingItemsSlice';
import { clearTryOnItems } from '../../../redux/slices/clothingItemsSlice';
import { clearSelectedVTOResult } from '../../../redux/slices/vtoSlice';
import { usePersonImage } from '../../../hooks/usePersonImage';
import { usePersonImages } from '../../../hooks/usePersonImages';
import { getSmallThumbnailUrl } from '../../../utils/imageUrl';

const WearingItemCard = ({ item, onRemove, disabled, onClick }) => {
  const [imgError, setImgError] = useState(false);

  // robust image source resolution — prefer AI product photo if available
  const rawSrc = item.generatedProductPhoto?.url || item.image?.url || item.image || item.imageUrl;
  const imgSrc = getSmallThumbnailUrl(rawSrc);

  // Handle card click - scroll to item in clothing panel
  const handleCardClick = (e) => {
    // Don't trigger if clicking the delete button
    if (e.target.closest('.delete-item-btn')) return;
    onClick && onClick(item);
  };

  return (
    <div
      className="clothing-item-card clickable"
      onClick={handleCardClick}
      title="Click to find in closet"
    >
      <div className="item-image-wrapper">
        {!imgError && imgSrc ? (
          <img
            src={imgSrc}
            alt={item.name || 'Clothing item'}
            className="item-image"
            onError={() => setImgError(true)}
          />
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#9ca3af',
            fontSize: '10px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px'
          }}>
            No image
          </div>
        )}
        {/* Delete button */}
        <button
          className="delete-item-btn"
          onClick={(e) => {
            e.stopPropagation();
            onRemove && onRemove(item);
          }}
          disabled={disabled}
          title="Remove this item"
        >
          <Icon name="close" />
        </button>
      </div>
    </div>
  );
};

const CurrentlyWearingSection = forwardRef(({ onRemoveItem, disabled, onOpenClothingPanel, onScrollToItem, panelPosition = 'bottom', hasClothingItems = true }, ref) => {
  const dispatch = useDispatch();
  const activeItems = useSelector(selectActiveItems);
  const personImageState = usePersonImage();
  const { activePersonImageId, personImages } = usePersonImages();


  // Determine if panel is on the right side (desktop)

  // Handle empty state click - open clothing panel and highlight it
  const handleEmptyClick = () => {
    onOpenClothingPanel && onOpenClothingPanel();
    window.dispatchEvent(new CustomEvent('highlightClothingPanel'));
  };

  const handleClearAll = () => {
    // Clear all active clothing items
    dispatch(clearTryOnItems());

    // Clear selected VTO result from history
    dispatch(clearSelectedVTOResult());

    // Get the current person image data to check useRemovedImage flag
    const currentPersonImage = personImages?.find(img => img.id === activePersonImageId);

    if (currentPersonImage) {
      const shouldUseRemoved = currentPersonImage.useRemovedImage !== false;
      const imageUrl = shouldUseRemoved && currentPersonImage.removedBgImage?.url
        ? currentPersonImage.removedBgImage.url
        : currentPersonImage.image?.url;

      // Set the correct version of the person image based on useRemovedImage flag
      personImageState.setVirtualTryOnResult({
        image: imageUrl,
        originalImage: imageUrl,
        metadata: {
          isVirtualTryOnResult: false,
          originalPersonImage: imageUrl
        }
      });

      // Then revert to clear VTO state
      personImageState.revertToOriginal();
    } else {
      // Fallback: just revert normally
      personImageState.revertToOriginal();
    }
  };

  // Handle individual item removal
  const handleRemoveItem = (item) => {
    if (onRemoveItem) {
      onRemoveItem(item);
    }
  };

  // Expose handleClearAll to parent via ref
  useImperativeHandle(ref, () => ({
    handleClearAll
  }));

  return (
    <div className="currently-wearing-section">
      <div className="section-header">
        <div className="section-label">Wearing</div>
        {activeItems.length > 0 && (
          <button
            className="clear-all-btn"
            onClick={handleClearAll}
            title="Clear all items and revert to original"
          >
            <Icon name="trash" style={{ margin: 0 }} />
          </button>
        )}
      </div>
      <div className={`items-grid ${activeItems.length > 2 ? 'has-many-items' : 'has-few-items'}`}>
        {activeItems.length > 0 ? (
          activeItems.map((item) => (
            <WearingItemCard
              key={item.id}
              item={item}
              onRemove={handleRemoveItem}
              disabled={disabled}
              onClick={onScrollToItem}
            />
          ))
        ) : (
          <div
            className="empty-state clickable"
            onClick={handleEmptyClick}
            title="Click to browse your closet"
          >
            <span>No items</span>
            <span className="empty-hint">Tap to select</span>
          </div>
        )}
      </div>


    </div>
  );
});

export default CurrentlyWearingSection;